import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { AiScreeningService } from '../../services/ai-screening.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-round-detail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './round-detail.component.html',
  styleUrl: './round-detail.component.scss'
})
export class RoundDetailComponent implements OnInit {
  roundId: string | null = null;
  jobId: string | null = null;
  roundDetails: any = null;
  isLoading = true;
  copied = false;

  filterFromDate: string = '';
  filterToDate: string = '';
  filteredAndSortedCandidates: any[] = [];
  showDatePicker = false;
  pickerStartDate: Date | null = null;
  pickerEndDate: Date | null = null;
  pickerHoverDate: Date | null = null;
  pickerViewDate: Date = new Date();

  showInviteModal = false;
  inviteEmails = '';
  isInviting = false;
  fromTab: string | null = null;

  showExtendExpiryModal = false;
  newExpiryDate = '';
  minExpiryDate = '';
  isExtending = false;
  selectedQuickExtend: number | null = null;
  isExporting = false;
  isSidebarCollapsed = false;

  showAdvancedFilters = false;

  advancedFilters = {
    name: '',
    email: '',
    phone: '',
    college: '',
    min_score: null as number | null,
    max_score: null as number | null,
    status: ''
  };

  appliedAdvancedFilters = {
    name: '',
    email: '',
    phone: '',
    college: '',
    min_score: null as number | null,
    max_score: null as number | null,
    status: ''
  };

  openAdvancedFiltersModal(): void {
    this.advancedFilters = { ...this.appliedAdvancedFilters };
    this.showAdvancedFilters = true;
  }

  closeAdvancedFiltersModal(): void {
    this.showAdvancedFilters = false;
  }

  applyAdvancedFilters(): void {
    this.appliedAdvancedFilters = { ...this.advancedFilters };
    this.showAdvancedFilters = false;
    this.processCandidates();
  }

  onFilterEnter(event: Event): void {
    const target = event.target as HTMLElement;
    if (target && target.tagName === 'BUTTON') {
      return;
    }
    event.preventDefault();
    this.applyAdvancedFilters();
  }

  clearAdvancedFilters(): void {
    this.advancedFilters = {
      name: '',
      email: '',
      phone: '',
      college: '',
      min_score: null,
      max_score: null,
      status: ''
    };
    this.appliedAdvancedFilters = { ...this.advancedFilters };
    this.showAdvancedFilters = false;
    this.processCandidates();
  }

  onClearFiltersFromButton(event: Event): void {
    event.stopPropagation();
    this.clearAdvancedFilters();
  }

  get activeFiltersCount(): number {
    let count = 0;
    const f = this.appliedAdvancedFilters;
    if (f.name && f.name.trim()) count++;
    if (f.email && f.email.trim()) count++;
    if (f.phone && f.phone.trim()) count++;
    if (f.college && f.college.trim()) count++;
    if (f.min_score !== null && f.min_score !== undefined && (f.min_score as any) !== '') count++;
    if (f.max_score !== null && f.max_score !== undefined && (f.max_score as any) !== '') count++;
    if (f.status && f.status.trim()) count++;
    return count;
  }

  get availableColleges(): string[] {
    if (!this.roundDetails?.candidates) return [];
    const set = new Set<string>();
    for (const c of this.roundDetails.candidates) {
      if (c.college_name && c.college_name.trim()) {
        set.add(c.college_name.trim());
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  expandSidebar(sectionId?: string): void {
    this.isSidebarCollapsed = false;
    if (sectionId) {
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 150);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private toastr: ToastrService,
    private aiScreeningService: AiScreeningService
  ) { }

  ngOnInit(): void {
    this._generateCalendarDays();
    this.roundId = this.route.snapshot.paramMap.get('roundId');
    this.jobId = this.route.snapshot.paramMap.get('id'); // jobId
    this.fromTab = this.route.snapshot.queryParamMap.get('from');

    if (this.roundId) {
      this.fetchRoundDetails(this.roundId);
    } else {
      this.toastr.error('Round ID missing');
      this.goBack();
    }

    const now = new Date();
    const tzOffset = now.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(now.getTime() - tzOffset)).toISOString().slice(0, 16);
    this.minExpiryDate = localISOTime;
  }

  fetchRoundDetails(id: string | number): void {
    this.isLoading = true;
    this.aiScreeningService.getRoundById(id).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && res.Success && res.Data) {
          this.roundDetails = res.Data;
          this.processCandidates();
        } else {
          this.toastr.error('Failed to load round details');
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        this.toastr.error('Failed to load round details');
      }
    });
  }

  processCandidates(): void {
    if (!this.roundDetails?.candidates) {
      this.filteredAndSortedCandidates = [];
      return;
    }

    let candidates = [...this.roundDetails.candidates];

    // Advanced Filters (applied simultaneously)
    const f = this.appliedAdvancedFilters;

    if (f.name && f.name.trim()) {
      const q = f.name.trim().toLowerCase();
      candidates = candidates.filter(c => (c.name || '').toLowerCase().includes(q));
    }

    if (f.email && f.email.trim()) {
      const q = f.email.trim().toLowerCase();
      candidates = candidates.filter(c => (c.email || '').toLowerCase().includes(q));
    }

    if (f.phone && f.phone.trim()) {
      const q = f.phone.trim().toLowerCase();
      candidates = candidates.filter(c => (c.phone || '').toLowerCase().includes(q));
    }

    if (f.college && f.college.trim()) {
      const targetCollege = f.college.trim().toLowerCase();
      // Exact match for college name so "SIT" matches ONLY SIT and not "SSIT"
      candidates = candidates.filter(c => (c.college_name || '').trim().toLowerCase() === targetCollege);
    }

    if (f.min_score !== null && f.min_score !== undefined && (f.min_score as any) !== '') {
      const minVal = Number(f.min_score);
      candidates = candidates.filter(c => c.score !== null && c.score !== undefined && Number(c.score) >= minVal);
    }

    if (f.max_score !== null && f.max_score !== undefined && (f.max_score as any) !== '') {
      const maxVal = Number(f.max_score);
      candidates = candidates.filter(c => c.score !== null && c.score !== undefined && Number(c.score) <= maxVal);
    }

    if (f.status && f.status.trim()) {
      const s = f.status.trim().toLowerCase();
      if (s === 'pass') {
        candidates = candidates.filter(c => c.is_passed === true || (c.status || '').toLowerCase() === 'pass');
      } else if (s === 'fail') {
        candidates = candidates.filter(c => c.is_passed === false || (c.status || '').toLowerCase() === 'fail');
      } else {
        candidates = candidates.filter(c => (c.status || '').toLowerCase() === s);
      }
    }

    if (this.filterFromDate) {
      const fromTimestamp = new Date(this.filterFromDate).setHours(0, 0, 0, 0);
      candidates = candidates.filter(c => {
        if (!c.submitted_date) return false;
        return new Date(c.submitted_date).getTime() >= fromTimestamp;
      });
    }

    if (this.filterToDate) {
      const toDateObj = new Date(this.filterToDate);
      toDateObj.setHours(23, 59, 59, 999);
      const toTimestamp = toDateObj.getTime();

      candidates = candidates.filter(c => {
        if (!c.submitted_date) return false;
        return new Date(c.submitted_date).getTime() <= toTimestamp;
      });
    }

    const completedStatuses = ['pass', 'passed', 'fail', 'failed', 'completed', 'graded'];

    candidates.sort((a, b) => {
      const statusA = (a.status || '').toLowerCase();
      const statusB = (b.status || '').toLowerCase();

      const isACompleted = completedStatuses.includes(statusA);
      const isBCompleted = completedStatuses.includes(statusB);

      if (isACompleted && !isBCompleted) return -1;
      if (!isACompleted && isBCompleted) return 1;

      if (isACompleted && isBCompleted) {
        const dateA = a.submitted_date ? new Date(a.submitted_date).getTime() : 0;
        const dateB = b.submitted_date ? new Date(b.submitted_date).getTime() : 0;
        return dateB - dateA;
      }

      return 0;
    });

    this.filteredAndSortedCandidates = candidates;
  }

  @HostListener('document:click')
  closeAll(): void {
    // picker closed by backdrop overlay
  }

  goBack(): void {
    if (this.jobId) {
      const queryParams: any = { subTab: 'interview' };
      if (this.fromTab) {
        queryParams.innerTab = this.fromTab;
      }
      this.router.navigate(['/recruiter-dashboard/jobs', this.jobId], { queryParams });
    } else {
      this.location.back();
    }
  }

  onCopy(): void {
    if (this.roundDetails?.screening_access_link) {
      navigator.clipboard.writeText(this.roundDetails.screening_access_link).then(() => {
        this.copied = true;
        this.toastr.success('Link copied to clipboard!');
        setTimeout(() => this.copied = false, 2000);
      });
    }
  }

  showReportWarningPopup = false;
  reportWarningTitle = '';
  reportWarningMessage = '';
  reportWarningType: 'info' | 'error' = 'info';
  expandedCandidateIds = new Set<string>();

  toggleAttemptsRow(candidateId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!candidateId) return;
    if (this.expandedCandidateIds.has(candidateId)) {
      this.expandedCandidateIds.delete(candidateId);
    } else {
      this.expandedCandidateIds.add(candidateId);
    }
  }

  isAttemptsExpanded(candidateId: string): boolean {
    return !!candidateId && this.expandedCandidateIds.has(candidateId);
  }

  openReportWarning(title: string, message: string, type: 'info' | 'error' = 'info') {
    this.reportWarningTitle = title;
    this.reportWarningMessage = message;
    this.reportWarningType = type;
    this.showReportWarningPopup = true;
  }

  closeReportWarning() {
    this.showReportWarningPopup = false;
  }

  viewCandidateReport(candidate: any, attempt?: any): void {
    const status = (attempt?.status || candidate?.status || '').toLowerCase();

    if (!['pass', 'passed', 'fail', 'failed', 'completed', 'graded', 'evaluated'].includes(status)) {
      let title = 'Report Unavailable';
      let message = 'This candidate has not yet completed the interview. The report will be available once it is finished.';
      let type: 'info' | 'error' = 'info';

      if (status === 'invited') {
        title = 'Invitation Sent';
        message = 'The candidate has been invited but has not yet started the interview.';
      } else if (status === 'verified') {
        title = 'Candidate Verified';
        message = 'The candidate is verified and preparing to start the interview.';
      } else if (status === 'in_progress') {
        title = 'Interview In Progress';
        message = 'The candidate is currently taking the interview. The report will be available once they finish.';
      }

      this.openReportWarning(title, message, type);
      return;
    }

    if (!attempt && candidate.report_link) {
      window.open(candidate.report_link, '_blank');
      return;
    }

    const sessionId = attempt
      ? (attempt.ai_session_id || attempt.interview_session_id)
      : (candidate.interview_session_id || candidate.front_end_session_id || candidate.session_id || candidate.id);
    const candidateId = candidate.candidate_id || candidate.id;

    let token = candidate.token;
    if (!token && this.roundDetails && this.roundDetails.screening_access_link) {
      const parts = this.roundDetails.screening_access_link.split('/');
      token = parts[parts.length - 2];
    }
    if (!token) token = 'none';

    if (sessionId && candidateId) {
      this.router.navigate(['/ai-performance-report', candidateId, sessionId, token]);
    } else {
      this.openReportWarning('Report Not Found', 'Interview session details are missing.', 'error');
    }
  }

  isExpired(round: any): boolean {
    if (!round?.valid_until) return false;
    return new Date(round.valid_until) < new Date();
  }

  openInviteModal(): void {
    this.showInviteModal = true;
    this.inviteEmails = '';
  }

  closeInviteModal(): void {
    this.showInviteModal = false;
    this.inviteEmails = '';
  }

  sendInvitations(): void {
    if (!this.inviteEmails.trim()) {
      this.toastr.warning('Please enter at least one email');
      return;
    }

    const emailsList = this.inviteEmails
      .split(/[\n,;]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);

    if (emailsList.length === 0) return;
    if (!this.roundId) return;

    this.isInviting = true;
    const payload = { emails: emailsList, phones: [] };

    this.aiScreeningService.inviteCandidatesToRound(this.roundId, payload).subscribe({
      next: (res: any) => {
        this.isInviting = false;
        if (res && res.Success) {
          this.toastr.success('Invitations sent successfully');
          this.closeInviteModal();
          this.fetchRoundDetails(this.roundId!);
        } else {
          this.toastr.error(res?.Message || 'Failed to send invitations');
        }
      },
      error: (err: any) => {
        this.isInviting = false;
        this.toastr.error('Failed to send invitations');
      }
    });
  }

  openExtendExpiryModal(): void {
    this.showExtendExpiryModal = true;

    if (this.roundDetails?.round?.valid_until) {
      const currentObj = new Date(this.roundDetails.round.valid_until);
      if (!isNaN(currentObj.getTime())) {
        const tzOffset = currentObj.getTimezoneOffset() * 60000;
        const localISOTime = (new Date(currentObj.getTime() - tzOffset)).toISOString().slice(0, 16);
        this.newExpiryDate = localISOTime;
        this.selectedQuickExtend = null;
        return;
      }
    }

    this.quickExtend(1);
  }

  closeExtendExpiryModal(): void {
    this.showExtendExpiryModal = false;
    this.newExpiryDate = '';
  }

  quickExtend(days: number): void {
    this.selectedQuickExtend = days;
    const now = new Date();
    now.setDate(now.getDate() + days);
    const tzOffset = now.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(now.getTime() - tzOffset)).toISOString().slice(0, 16);
    this.newExpiryDate = localISOTime;
  }

  extendExpiry(): void {
    if (!this.newExpiryDate) {
      this.toastr.warning('Please select a valid expiry date');
      return;
    }

    if (!this.roundId) return;

    this.isExtending = true;

    let valid_until_iso = '';
    try {
      valid_until_iso = new Date(this.newExpiryDate).toISOString();
    } catch (e) {
      this.toastr.error('Invalid date selected');
      this.isExtending = false;
      return;
    }

    const payload = { valid_until: valid_until_iso };

    this.aiScreeningService.updateScreeningRound(this.roundId, payload).subscribe({
      next: (res: any) => {
        this.isExtending = false;
        if (res && res.Success) {
          this.toastr.success('Round expiry updated successfully');
          this.closeExtendExpiryModal();
          this.fetchRoundDetails(this.roundId!);
        } else {
          this.toastr.error(res?.Message || 'Failed to update expiry');
        }
      },
      error: (err: any) => {
        this.isExtending = false;
        this.toastr.error('Failed to update expiry');
      }
    });
  }

  exportCandidatesReport(): void {
    if (!this.roundId) return;
    
    if (!this.filteredAndSortedCandidates || this.filteredAndSortedCandidates.length === 0) {
      this.toastr.warning('No candidates to export based on current filters');
      return;
    }

    try {
      this.isExporting = true;
      
      const exportData = this.filteredAndSortedCandidates.map(c => {
        const latestAttempt = (c.attempts_history && c.attempts_history.length > 0) ? c.attempts_history[0] : null;
        const timeSpent = c.time_spent || latestAttempt?.time_spent || '-';
        const attemptsCount = c.attempts !== null && c.attempts !== undefined ? c.attempts : (c.attempts_history?.length || 0);
        const rawStatus = c.is_passed ? 'Pass' : (c.status === 'fail' ? 'Fail' : (c.status || '-'));
        const formattedStatus = typeof rawStatus === 'string'
          ? (rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase())
          : rawStatus;

        return {
          'Candidate Name': c.name || 'Anonymous Candidate',
          'Email': c.email || '',
          'Phone': c.phone || '',
          'College': c.college_name || '',
          'Status': formattedStatus,
          'Score': c.score !== null && c.score !== undefined ? c.score : 0,
          'Attempts': attemptsCount,
          'Time Spent': timeSpent
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Candidates');
      
      const roundName = this.roundDetails?.round?.job_role || 'round';
      const fileName = `${roundName.replace(/\s+/g, '_')}_candidates.xlsx`;
      
      XLSX.writeFile(workbook, fileName);
      this.toastr.success('Candidates report exported successfully');
    } catch (err) {
      this.toastr.error('Failed to export candidates report');
      console.error('Export error:', err);
    } finally {
      this.isExporting = false;
    }
  }

  get pickerDisplayLabel(): string {
    if (!this.pickerStartDate) return 'Select date range';
    const fmt = (d: Date) => `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
    if (!this.pickerEndDate) return fmt(this.pickerStartDate);
    return `${fmt(this.pickerStartDate)} – ${fmt(this.pickerEndDate)}`;
  }

  get pickerMonthLabel(): string {
    return this.pickerViewDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  get pickerWeekDays(): string[] {
    return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  }

  pickerCalendarDays: (Date | null)[] = [];



  private _generateCalendarDays(): void {
    const year = this.pickerViewDate.getFullYear();
    const month = this.pickerViewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    this.pickerCalendarDays = cells;
  }

  pickerPrevMonth(): void {
    const d = new Date(this.pickerViewDate);
    d.setMonth(d.getMonth() - 1);
    this.pickerViewDate = d;
    this._generateCalendarDays();
  }

  pickerNextMonth(): void {
    const d = new Date(this.pickerViewDate);
    d.setMonth(d.getMonth() + 1);
    this.pickerViewDate = d;
    this._generateCalendarDays();
  }

  pickerSelectDay(day: Date | null): void {
    if (!day) return;
    if (!this.pickerStartDate || (this.pickerStartDate && this.pickerEndDate)) {
      this.pickerStartDate = day;
      this.pickerEndDate = null;
    } else {
      if (day < this.pickerStartDate) {
        this.pickerEndDate = this.pickerStartDate;
        this.pickerStartDate = day;
      } else {
        this.pickerEndDate = day;
      }
    }
  }

  pickerIsStart(day: Date | null): boolean {
    if (!day || !this.pickerStartDate) return false;
    return this._sameDay(day, this.pickerStartDate);
  }

  pickerIsEnd(day: Date | null): boolean {
    if (!day || !this.pickerEndDate) return false;
    return this._sameDay(day, this.pickerEndDate);
  }

  pickerIsInRange(day: Date | null): boolean {
    if (!day || !this.pickerStartDate) return false;
    const end = this.pickerEndDate || this.pickerHoverDate;
    if (!end) return false;
    const s = this.pickerStartDate < end ? this.pickerStartDate : end;
    const e = this.pickerStartDate < end ? end : this.pickerStartDate;
    return day >= s && day <= e;
  }

  pickerIsToday(day: Date | null): boolean {
    if (!day) return false;
    return this._sameDay(day, new Date());
  }

  private _sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  private _toDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  applyDatePicker(): void {
    if (this.pickerStartDate) {
      this.filterFromDate = this._toDateStr(this.pickerStartDate);
      this.filterToDate = this.pickerEndDate
        ? this._toDateStr(this.pickerEndDate)
        : this._toDateStr(this.pickerStartDate);
    }
    this.showDatePicker = false;
    this.processCandidates();
  }

  clearDatePicker(): void {
    this.pickerStartDate = null;
    this.pickerEndDate = null;
    this.pickerHoverDate = null;
    this.filterFromDate = '';
    this.filterToDate = '';
    this.showDatePicker = false;
    this.processCandidates();
  }

  toggleDatePicker(event: Event): void {
    event.stopPropagation();
    this.showDatePicker = !this.showDatePicker;
  }


}
